import { Box, Select, MenuItem, TextField, IconButton } from '@mui/material';
import { Clear, Add, Edit, Close } from '@mui/icons-material';
import { BindingsTableProps } from './BindingsTable.types';
import { validateDistance } from '../utils/bindingUtils';

import './BindingsTable.css';

/**
 * Renders an editable table for managing station bindings
 * Displays OKKO station and competitor stations with editable binding properties
 *
 @param props - The component props
 @returns JSX element containing the bindings management table
 */
export function BindingsTable({
  bindingDetails,
  influenceTypes,
  locationTypes,
  resetting = false,
  readOnly = false,
  onDeleteBinding,
  onDeleteAll,
  onUpdateField
}: BindingsTableProps) {
  const okkoDetail = bindingDetails.find((d: any) => d.isOkko);

  return (
    <Box className="bindings-table-container">
      {!readOnly && (
        <Box className="delete-all-button">
          <IconButton onClick={onDeleteAll} title="Видалити усі зв'язки із цією АЗК ОККО" className="delete-all-icon">
            <Clear className="delete-all-icon" />
          </IconButton>
        </Box>
      )}

      <Box className="bindings-table">
        <Box className="bindings-table-header">
          <Box className="bindings-header-cell azk-binding-col-5"></Box>
          <Box className="bindings-header-cell azk-binding-col-8">Юнікод</Box>
          <Box className="bindings-header-cell azk-binding-col-40">Адреса</Box>
          <Box className="bindings-header-cell azk-binding-col-15">Бренд АЗК</Box>
          <Box className="bindings-header-cell azk-binding-col-12">Тип впливу</Box>
          <Box className="bindings-header-cell azk-binding-col-15">Тип розміщення</Box>
          <Box className="bindings-header-cell azk-binding-col-10">Відстань до АЗК</Box>
          <Box className="bindings-header-cell azk-binding-col-10">Відстань по прямій</Box>
          <Box className="bindings-header-cell azk-binding-col-5">Дії</Box>
        </Box>

        {okkoDetail && (
          <Box className="bindings-data-row okko-row">
            <Box className="bindings-data-cell actions-cell azk-binding-col-5"></Box>
            <Box className="bindings-data-cell azk-binding-col-8">{okkoDetail.unicode}</Box>
            <Box className="bindings-data-cell azk-binding-col-40">{okkoDetail.address}</Box>
            <Box className="bindings-data-cell azk-binding-col-15">
              <Box className="brand-badge okko">{okkoDetail.brand}</Box>
            </Box>
            <Box className="bindings-data-cell azk-binding-col-12"></Box>
            <Box className="bindings-data-cell azk-binding-col-15"></Box>
            <Box className="bindings-data-cell azk-binding-col-10"></Box>
            <Box className="bindings-data-cell azk-binding-col-10"></Box>
            <Box className="bindings-data-cell azk-binding-col-5"></Box>
          </Box>
        )}

        {resetting ? (
          <Box className="bindings-data-row">
            <Box className="bindings-data-cell">Скидання змін...</Box>
          </Box>
        ) : (
          bindingDetails
            .filter((detail: any) => !detail.isOkko)
            .map((detail: any) => (
              <Box key={detail.id} className={`bindings-data-row  ${detail.status || ''}`}>
                <Box className="bindings-data-cell actions-cell azk-binding-col-5">
                  {!readOnly && (
                    <IconButton
                      onClick={() => onDeleteBinding(detail.id)}
                      title="Видалити цю АЗК із зв'язки"
                      className="delete-binding-button"
                    >
                      <Clear className="delete-binding-icon" />
                    </IconButton>
                  )}
                </Box>
                <Box className="bindings-data-cell azk-binding-col-8">{detail.unicode}</Box>
                <Box className="bindings-data-cell azk-binding-col-40">{detail.address}</Box>
                <Box className="bindings-data-cell azk-binding-col-15">
                  <Box className="brand-badge competitor">{detail.brand}</Box>
                </Box>
                <Box className="bindings-data-cell azk-binding-col-12">
                  <Select
                    value={influenceTypes.some((t: any) => t.value === detail.influence_type)
                      ? detail.influence_type
                      : influenceTypes.find((t: any) => t.label === detail.influence_type)?.value || ''}
                    onChange={(e: any) => onUpdateField(detail.id, 'influence_type', e.target.value)}
                    size="small"
                    disabled={readOnly}
                    className="binding-select influence-select"
                  >
                    <MenuItem value=""></MenuItem>
                    {influenceTypes.map((type: any) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box className="bindings-data-cell azk-binding-col-15">
                  <Select
                    value={locationTypes.some((t: any) => t.value === detail.location_type)
                      ? detail.location_type
                      : locationTypes.find((t: any) => t.label === detail.location_type)?.value || ''}
                    onChange={(e: any) => onUpdateField(detail.id, 'location_type', e.target.value)}
                    size="small"
                    disabled={readOnly}
                    className="binding-select placement-select"
                  >
                    <MenuItem value=""></MenuItem>
                    {locationTypes.map((type: any) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box className="bindings-data-cell azk-binding-col-10">
                  <TextField
                    value={detail.distance_to_azk || ''}
                    onChange={(e: any) => onUpdateField(detail.id, 'distance_to_azk', validateDistance(e.target.value))}
                    placeholder="0,0"
                    size="small"
                    disabled={readOnly}
                    className="binding-input distance-input"
                  />
                </Box>
                <Box className="bindings-data-cell azk-binding-col-10">
                  {detail.straight_distance ? `${detail.straight_distance} км` : '-'}
                </Box>
                <Box className="bindings-data-cell actions-cell azk-binding-col-5">
                  <Box className="status-icons">
                    {detail.type === 'new' && <Add className="status-icon added" />}
                    {detail.status === 'edited' && <Edit className="status-icon edited" />}
                    {detail.status === 'deleted' && <Close className="status-icon deleted" />}
                  </Box>
                </Box>
              </Box>
            ))
        )}
      </Box>
    </Box>
  );
}
